import GroupSearchLink from "../../components/buttons/GroupSearchLink";
import GroupSkeleton from "../../components/loading/GroupSkeleton";
import React, {useEffect, useRef, useState} from "react";
import {motion} from "motion/react";
import {useNavigate} from "react-router-dom";
import {GroupSessionResponse} from "../../models/GroupSessionResponse";
import {GroupSessionService} from "../../services/GroupSessionService";
import {PlaneIcon} from "../../assets/svgComponents/PlaneIcon";
import {DecorationUnderlineShort} from "../../assets/svgComponents/inviteFriendsPage/DecorationUnderlineShort";
import SessionCard from "../../components/cards/SessionCard";
import {getCurrentUserId} from "../../utils/auth_utils";
import InviteSessionCard from "../../components/cards/SessionInviteCard";

// ==== Props ====
interface GroupSessionPageProps {
    userEmail: string | null;
    username: string | null;
    onLogout: () => void;
}

// ==== Helpers ====
const firstNameFromEmail = (email?: string | null) =>
    (email || "").split("@")[0]?.replace(/\./g, " ") || "there";

const LogoutIcon = ({className = ""}) => (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden>
        <path
            d="M16 13v-2H7V8l-5 4 5 4v-3h9zM20 3h-8a2 2 0 0 0-2 2v3h2V5h8v14h-8v-3h-2v3a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2z"/>
    </svg>
);

const GroupSessionPage: React.FC<GroupSessionPageProps> = ({userEmail, username, onLogout}) => {
    const [sessions, setSessions] = useState<GroupSessionResponse[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const navigate = useNavigate();

    const currentUserId = getCurrentUserId();

    const scrollerRef = useRef<HTMLUListElement>(null);

    const fetchSessions = async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await GroupSessionService.listSessions();
            setSessions(data || []);
        } catch (err: any) {
            setError(err.message || "Failed to load sessions.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSessions();
    }, []);

    const handleCreateSession = () => navigate("/sessions/create");

    const handleOpen = (s: GroupSessionResponse) => {
        if (s.status === "INVITED") {
            navigate(`/sessions/${s.sessionId}/join`);
            return;
        }
        if (s.status === "COMPLETED") {
            navigate(`/sessions/${s.sessionId}/unveil`);
        } else {
            navigate(`/sessions/${s.sessionId}`);
        }
    };

    const handleDeleteSession = async (sessionId: string, e: React.MouseEvent) => {
        e.stopPropagation();
        if (!window.confirm("Delete this session?")) return;
        try {
            await GroupSessionService.deleteSession(sessionId);
            setSessions((prev) => prev.filter((x) => x.sessionId !== sessionId));
        } catch (err: any) {
            setError("Failed to delete session.");
        }
    };

    const handleDeclineInvitation = async (sessionId: string) => {
        try {
            await GroupSessionService.declineInvitation(sessionId);
            setSessions((prev) => prev.filter((x) => x.sessionId !== sessionId));
        } catch (err: any) {
            setError(err.message || "Failed to decline invitation.");
        }
    };

    const firstName = username || firstNameFromEmail(userEmail);

    return (
        <motion.div
            className="min-h-screen w-full px-4 sm:px-8 lg:px-16 font-outfit bg-gradient-to-b from-[#f6f8ff] to-[#dde5ff] relative overflow-hidden"
            initial={{opacity: 0}}
            animate={{opacity: 1}}
            transition={{duration: 0.35}}
        >
            {/* Header */}
            <header className="gap-3 px-2 sm:px-10 pt-8 grid grid-cols-[1fr_auto_1fr] items-center">
                <div className="flex items-center gap-3">
                    <div className="rounded-2xl bg-violet-500/90 text-white p-3 shadow">
                        <PlaneIcon/>
                    </div>
                </div>

                <GroupSearchLink/>

                <div className="flex min-w-0 items-center justify-end gap-2 sm:gap-3">
                    <span title={userEmail || undefined} className="hidden min-w-0 truncate text-sm text-gray-600 md:block">{userEmail}</span>
                    <button
                        type="button"
                        aria-label="Log out"
                        className="inline-flex items-center gap-2 shrink-0 rounded-2xl bg-white px-3 sm:px-4 py-2 shadow-sm hover:shadow text-gray-700"
                        onClick={onLogout}
                    >
                        <LogoutIcon className="w-4 h-4"/>
                        <span className="hidden text-sm font-semibold sm:inline">Log out</span>
                    </button>
                </div>
            </header>

            {/* Hero */}
            <div className="relative px-6 sm:px-10 mt-10">
                <h1 className="text-[36px] sm:text-[52px] font-bold tracking-tight leading-tight text-[#232323]">
                    Welcome away, <br/>
                    {firstName}.
                </h1>
                <div className="absolute bottom-0 left-9 w-fit">
                    <DecorationUnderlineShort/>
                </div>
            </div>

            {/* Error / Loading */}
            {error && (
                <div
                    className="mx-6 sm:mx-10 mt-4 rounded-xl bg-rose-50 text-rose-700 px-4 py-3 border border-rose-100">
                    {error}
                </div>
            )}
            {loading && (
                <div className="px-6 sm:px-10 mt-16"><h2 className="mb-3 text-lg font-semibold text-[#C0C4D8]">Sessions</h2><GroupSkeleton variant="sessions" label="Deine Reisen werden geladen"/></div>
            )}

            {/* Single unified section (own + invited inline) */}
            {!loading && sessions.length > 0 && (
                <section className="px-6 sm:px-10 mt-16">
                    <div className="flex items-center justify-between mb-3">
                        <h2 className="text-lg font-semibold text-[#C0C4D8]">Sessions</h2>
                    </div>

                    <ul
                        ref={scrollerRef}
                        className="no-scrollbar flex overflow-x-auto snap-x snap-mandatory pb-2 list-none"
                    >
                        {sessions.map((s) => {
                            const isInvite = s.status === "INVITED";
                            const isOwner = !!currentUserId && s.creatorUserId === currentUserId;

                            if (isInvite) {
                                return (
                                    <InviteSessionCard
                                        key={`invite-${s.sessionId}`}
                                        s={s}
                                        onAccept={() => navigate(`/sessions/${s.sessionId}/join`)}
                                        onDecline={() => handleDeclineInvitation(s.sessionId)}
                                        onOpen={() => navigate(`/sessions/${s.sessionId}/join`)}
                                    />
                                );
                            }

                            // Default card for non-invited sessions
                            return (
                                <SessionCard
                                    key={s.sessionId}
                                    s={s}
                                    isOwner={isOwner}
                                    onOpen={() => handleOpen(s)}
                                    onRemove={(e) => handleDeleteSession(s.sessionId, e)}
                                />
                            );
                        })}

                        {/* Add Card */}
                        <li className="snap-start shrink-0 w-[100px] ml-2">
                            <button
                                onClick={handleCreateSession}
                                className="h-full w-full rounded-3xl bg-white/60 backdrop-blur text-violet-500 hover:bg-white/80 hover:border-violet-400 transition flex items-center justify-center text-4xl font-bold"
                                title="Create new session"
                            >
                                <svg width="25" height="25" viewBox="0 0 25 25" fill="none"
                                     xmlns="http://www.w3.org/2000/svg">
                                    <path
                                        d="M22.9167 10.4167H14.5833V2.08333C14.5833 1.5308 14.3638 1.0009 13.9731 0.610195C13.5824 0.219494 13.0525 0 12.5 0C11.9475 0 11.4176 0.219494 11.0269 0.610195C10.6362 1.0009 10.4167 1.5308 10.4167 2.08333V10.4167H2.08333C1.5308 10.4167 1.0009 10.6362 0.610195 11.0269C0.219494 11.4176 0 11.9475 0 12.5C0 13.0525 0.219494 13.5824 0.610195 13.9731C1.0009 14.3638 1.5308 14.5833 2.08333 14.5833H10.4167V22.9167C10.4167 23.4692 10.6362 23.9991 11.0269 24.3898C11.4176 24.7805 11.9475 25 12.5 25C13.0525 25 13.5824 24.7805 13.9731 24.3898C14.3638 23.9991 14.5833 23.4692 14.5833 22.9167V14.5833H22.9167C23.4692 14.5833 23.9991 14.3638 24.3898 13.9731C24.7805 13.5824 25 13.0525 25 12.5C25 11.9475 24.7805 11.4176 24.3898 11.0269C23.9991 10.6362 23.4692 10.4167 22.9167 10.4167Z"
                                        fill="#787878"
                                    />
                                </svg>
                            </button>
                        </li>
                    </ul>
                </section>
            )}

            {/* Empty state */}
            {!loading && !error && sessions.length === 0 && (
                <div className="px-6 sm:px-10 mt-16">
                    <button
                        onClick={handleCreateSession}
                        className="h-[300px] w-[100px] rounded-3xl bg-white/60 backdrop-blur text-violet-500 hover:bg-white/80 hover:border-violet-400 transition flex items-center justify-center text-4xl font-bold"
                        title="Create new session"
                    >
                        <svg width="25" height="25" viewBox="0 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path
                                d="M22.9167 10.4167H14.5833V2.08333C14.5833 1.5308 14.3638 1.0009 13.9731 0.610195C13.5824 0.219494 13.0525 0 12.5 0C11.9475 0 11.4176 0.219494 11.0269 0.610195C10.6362 1.0009 10.4167 1.5308 10.4167 2.08333V10.4167H2.08333C1.5308 10.4167 1.0009 10.6362 0.610195 11.0269C0.219494 11.4176 0 11.9475 0 12.5C0 13.0525 0.219494 13.5824 0.610195 13.9731C1.0009 14.3638 1.5308 14.5833 2.08333 14.5833H10.4167V22.9167C10.4167 23.4692 10.6362 23.9991 11.0269 24.3898C11.4176 24.7805 11.9475 25 12.5 25C13.0525 25 13.5824 24.7805 13.9731 24.3898C14.3638 23.9991 14.5833 23.4692 14.5833 22.9167V14.5833H22.9167C23.4692 14.5833 23.9991 14.3638 24.3898 13.9731C24.7805 13.5824 25 13.0525 25 12.5C25 11.9475 24.7805 11.4176 24.3898 11.0269C23.9991 10.6362 23.4692 10.4167 22.9167 10.4167Z"
                                fill="#787878"
                            />
                        </svg>
                    </button>
                </div>
            )}
        </motion.div>
    );
};

export default GroupSessionPage;
